import React, { useMemo, useState } from 'react';
import {
  ArrowRightOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import {
  DashboardActivity,
  DashboardActivityFilter,
} from '../models/dashboard.model';

interface Props {
  activities: DashboardActivity[];
  dateLabel: string;
  onOpenActivity: (applicationId: string) => void;
  onViewAgenda: () => void;
}

const FILTER_LABELS: Record<DashboardActivityFilter, string> = {
  all: 'All',
  interview: 'Interviews',
  'follow-up': 'Follow-ups',
};

const DailyActivitiesPanel: React.FC<Props> = ({
  activities,
  dateLabel,
  onOpenActivity,
  onViewAgenda,
}) => {
  const [filter, setFilter] = useState<DashboardActivityFilter>('all');
  const counts = useMemo(
    () => ({
      all: activities.length,
      interview: activities.filter((item) => item.kind === 'interview').length,
      'follow-up': activities.filter((item) => item.kind === 'follow-up')
        .length,
    }),
    [activities]
  );
  const visibleActivities =
    filter === 'all'
      ? activities
      : activities.filter((activity) => activity.kind === filter);

  return (
    <section
      className="dashboard-panel dashboard-activities"
      aria-labelledby="today-activities-title"
    >
      <header className="dashboard-panel__header dashboard-activities__header">
        <div>
          <h2 id="today-activities-title">Today</h2>
          <span>
            {dateLabel} · {activities.length}{' '}
            {activities.length === 1 ? 'activity' : 'activities'}
          </span>
        </div>
        <button className="dashboard-link" type="button" onClick={onViewAgenda}>
          Interview Agenda <ArrowRightOutlined aria-hidden />
        </button>
      </header>

      <div
        className="dashboard-activity-filters"
        role="group"
        aria-label="Filter today’s activities"
      >
        {(Object.keys(FILTER_LABELS) as DashboardActivityFilter[]).map(
          (filterKey) => (
            <button
              className={`dashboard-activity-filter${
                filter === filterKey ? ' dashboard-activity-filter--active' : ''
              }`}
              type="button"
              aria-pressed={filter === filterKey}
              onClick={() => setFilter(filterKey)}
              key={filterKey}
            >
              {FILTER_LABELS[filterKey]} {counts[filterKey]}
            </button>
          )
        )}
      </div>

      {visibleActivities.length === 0 ? (
        <div className="dashboard-empty dashboard-empty--compact" role="status">
          <CheckCircleOutlined aria-hidden />
          <strong>
            {filter === 'all'
              ? 'Your day is clear'
              : `No ${FILTER_LABELS[filter].toLowerCase()} today`}
          </strong>
          <span>
            {filter === 'all'
              ? 'There are no interviews or follow-ups requiring attention.'
              : 'Choose another activity filter to continue.'}
          </span>
        </div>
      ) : (
        <ol className="dashboard-activity-list">
          {visibleActivities.slice(0, 5).map((activity) => (
            <li key={activity.id}>
              <button
                className="dashboard-activity"
                type="button"
                onClick={() => onOpenActivity(activity.applicationId)}
              >
                <time>{activity.timeLabel}</time>
                <span
                  className={`dashboard-activity__icon dashboard-activity__icon--${activity.kind}`}
                  aria-hidden
                >
                  {activity.kind === 'interview' ? (
                    <CalendarOutlined />
                  ) : (
                    <ClockCircleOutlined />
                  )}
                </span>
                <span className="dashboard-activity__copy">
                  <strong>{activity.title}</strong>
                  <small>{activity.meta}</small>
                </span>
                <span
                  className={`dashboard-activity__status dashboard-activity__status--${activity.kind}`}
                >
                  {activity.statusLabel}
                </span>
              </button>
            </li>
          ))}
        </ol>
      )}

      <footer className="dashboard-activities__footer">
        <span>
          {visibleActivities.length > 5
            ? `${visibleActivities.length - 5} more activities`
            : 'Keep interviews and follow-ups moving'}
        </span>
        <button
          className="dashboard-secondary-action"
          type="button"
          onClick={onViewAgenda}
        >
          View full day
        </button>
      </footer>
    </section>
  );
};

export default DailyActivitiesPanel;
